import { faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { usePdfTemplateExtraction } from "@hooks/usePdfTemplateExtraction";
import { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";

import { TEMPLATE_TYPES } from "../../autotemplates/constants";
import PdfExtractionLoading from "./vetFlow/TemplateSetupStep/PdfExtractionLoading";
import FileUploader from "../../autotemplates/TemplateBuilder/components/FileUploader";
import TemplateFields from "../../autotemplates/TemplateBuilder/components/TemplateFields";
import { alert } from "../../common/Alert";
import StepWrapper from "../StepWrapper";

export default function TemplateSetupStep({
  templateType,
  methods,
  onLoadingChange,
  onTemplateLoaded,
}) {
  const [fields, setFields] = useState(null);
  const isSoapSelected =
    String(templateType || "").toLowerCase() === "soap";
  const effectiveTemplateType = isSoapSelected
    ? TEMPLATE_TYPES.SOAP
    : TEMPLATE_TYPES.CUSTOM;
  const { extractTemplateFromPdf, loading } =
    usePdfTemplateExtraction();

  // Sync normalized template type into the form so TemplateFields
  // reads the correct value (onboarding stores lowercase "soap"/"non-soap")
  useEffect(() => {
    methods.setValue("templateType", effectiveTemplateType);
  }, [effectiveTemplateType, methods]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const onFileUpload = async (file) => {
    extractTemplateFromPdf({
      file,
      templateType: effectiveTemplateType,
      onExtract: (updatedFields) => {
        methods.setValue("templateFields", updatedFields, {
          shouldDirty: true,
        });
        setFields(updatedFields);
        onTemplateLoaded?.(true);
      },
    }).catch(() => {
      alert(
        "error",
        "There was an error extracting the text from your PDF. Try again or use a different PDF.",
      );
    });
  };

  return (
    <StepWrapper
      title={
        fields
          ? "Here's Your Template!"
          : loading
            ? "We're converting your template into text. This should only take a few seconds."
            : "Upload a PDF of Your Template."
      }
      description={
        fields ? (
          "Review and make any edits you need. You can always adjust this template later."
        ) : loading ? (
          <FontAwesomeIcon
            icon={faSpinner}
            className="animate-spin"
          />
        ) : (
          "We'll extract the structure so you can edit it below. You can always adjust or upload a different template later."
        )
      }
      withBackground={false}
    >
      <div className="w-full">
        <FormProvider {...methods}>
          {loading ? (
            <PdfExtractionLoading />
          ) : !fields ? (
            <div className="bg-white/10 rounded-xl backdrop-blur-xs shadow-lg">
              <FileUploader
                onFileUpload={onFileUpload}
                onboardingMode={true}
              />
            </div>
          ) : (
            fields && (
              <div className="bg-gray-900 rounded-xl backdrop-blur-xs shadow-lg max-h-[64vh] overflow-y-auto p-6">
                <TemplateFields
                  control={methods.control}
                  onboardingMode={true}
                />
              </div>
            )
          )}
        </FormProvider>
      </div>
    </StepWrapper>
  );
}
